/**
 * AI修饰API
 * 用于优化用户的提示词，生成详细的画面脚本
 */

import Logger from "../utils/logger";
import { API_PATHS } from "./config";
import { getCurrentUser, requestManager, getCustomClientSlug, requestModelByModeConfig } from "./utils";

/**
 * 流式调用AI修饰接口
 * @param {string} prompt - 用户的原始提示词
 * @param {Array} conversationHistory - 对话历史
 * @param {string} targetMode - 目标模式（manim/threejs/geogebra等），用于获取对应的AI修饰提示词
 * @param {function} onChunkReceived - 每当接收到一个文本块时的回调
 * @param {function} onComplete - 当完成整个响应时的回调
 * @param {function} onError - 发生错误时的回调
 * @param {string} apiMode - API模式，默认为 "prompt_enhance"，也可为 "companion"
 */
export const streamPromptEnhance = async (
  prompt,
  conversationHistory = [],
  targetMode = "manim",
  onChunkReceived,
  onComplete,
  onError,
  apiMode = "prompt_enhance"
) => {
  Logger.info("开始AI调用", {
    endpoint: `${API_PATHS.BASE_API_URL}${API_PATHS.PROXY.MODEL}`,
    mode: apiMode,
    promptLength: prompt.length,
    historyLength: conversationHistory.length,
  });

  try {
    await requestManager.enqueue(async () => {
      try {
        // 构建请求体
        // 兼容处理：如果 apiMode 是 companion，则 request mode 仍为 prompt_enhance
        // target_mode 始终保持原有的 targetMode (如 manim, threejs 等)，不再强制改为 companion
        // 这样可以复用原有的锤锤帮忙（AI优化）的后端逻辑和数据库记录
        const requestMode = apiMode === "companion" ? "prompt_enhance" : apiMode;
        const finalTargetMode = targetMode; 

        const requestBody = {
          mode: requestMode,
          prompt: prompt,
          conversation_history: conversationHistory,
          target_mode: finalTargetMode, // 目标模式
          stream: false,
        };

        // 添加用户ID
        const currentUser = getCurrentUser();
        requestBody.userId = currentUser?.id || 0;
        const slug = getCustomClientSlug();
        if (slug) requestBody.custom_client_slug = slug;

        Logger.debug("发送AI修饰请求", requestBody);

        const { fullResponse, chargeSession } = await requestModelByModeConfig(requestBody, onChunkReceived);

        Logger.info("AI修饰API调用完成", {
          responseLength: fullResponse.length,
        });

        if (onComplete) {
          onComplete(fullResponse, chargeSession);
        }

        Logger.logApiCall(
          `${API_PATHS.BASE_API_URL}${API_PATHS.PROXY.MODEL}`,
          { prompt, mode: apiMode },
          "success",
          { responseLength: fullResponse.length }
        );
      } catch (error) {
        Logger.error("AI调用失败", error);
        Logger.logApiCall(
          `${API_PATHS.BASE_API_URL}${API_PATHS.PROXY.MODEL}`,
          { prompt, mode: apiMode },
          "error",
          { error: error.message }
        );
        if (onError) {
          onError(error.message);
        }
      }
    });
  } catch (error) {
    Logger.error("AI修饰请求队列处理失败", error);
    if (onError) {
      onError(`请求队列处理失败: ${error.message}`);
    }
  }
};

export default {
  streamPromptEnhance,
};

